import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { SC_LINE_MODS, SOURCE, scLineModAllowed } from '../../lib/scLineMods';

import ProductSwap from './productSwap/index';
import UpdateVariant from './updateVariant';
import BundleUpdate from './BundleUpdate';
import ActionsMenu from '../components/actionsMenu';
import { ProductSwapTypes } from '../utils/constants';
import useProductContext, { ProductContext } from './state/ProductContext';

import useGlobalModalContext from '../state/GlobalModalContext';
import { EditButton } from '../components/actionsMenu/actionsMenu';
import { getIdFromGid } from '../utils';
import { getBundleByProductId } from './api';
import { useGlobalContext } from './state/GlobalContext';
import { itemIsFreeProduct, transformProductsInBundle } from './utils';
import { formatBundleCollections } from './BundleUpdate';

let publishFn = (data: any) => void 0;

const useLaunchModal = ({
  product,
  productSwapModal,
  sellingPlans,
  updateBundle,
  swapProductForBundle,
  updateProduct,
  closeModal,
  subscription,
  shopConfiguration,
  bundleData,
  isLoading,
}) => {
  const { t } = useTranslation();
  const productType = productSwapModal as ProductSwapTypes;
  const { handleModalContent, isGlobalModalOpen } = useGlobalModalContext();
  const editBundleModalTitle = t('bundles.edit_my_box');
  const productSwapModalTitle = t('product_swap.title_select');

  const subscriber = (callback) => {
    publishFn = callback;
    return () => {
      publishFn = () => void 0;
    };
  };

  React.useEffect(() => {
    if (bundleData && !isLoading) {
      publishFn(bundleData);
    }
  }, [bundleData, isLoading]);

  const getModalOptions = React.useCallback(() => {
    const handleProductChange = (
      productId,
      quantity,
      lineId,
      productUpdated,
    ) => {
      updateProduct(productId, quantity, lineId, productUpdated);
      closeModal();
    };

    const handleBundleChange = (
      newBundleProps,
      lineId: string,
      dynamicBabItems?: unknown,
      sellingPlan?: SellingPlan,
      discountCodes?: string[],
      oneTimeAddon?: boolean,
    ) => {
      swapProductForBundle(
        newBundleProps,
        lineId,
        dynamicBabItems,
        sellingPlan,
        discountCodes,
        oneTimeAddon,
      );
      closeModal();
    };

    return {
      [ProductSwapTypes.BUNDLE]: {
        content: () => (
          <BundleUpdate
            bundle={bundleData}
            subscription={subscription}
            product={product}
            closeModal={closeModal}
            updateBundle={updateBundle}
            shopConfiguration={shopConfiguration}
            subscriber={subscriber}
          />
        ),
        title: editBundleModalTitle,
      },
      [ProductSwapTypes.PRODUCT]: {
        content: () => (
          <ProductSwap
            product={product}
            subscription={subscription}
            updateProduct={handleProductChange}
            sellingPlans={sellingPlans}
            updateForBundle={handleBundleChange}
            shopConfiguration={shopConfiguration}
            closeModal={closeModal}
            subscriptionCurrencyCode={subscription.CurrencyCode}
          />
        ),
        title: productSwapModalTitle,
      },
    };
  }, [
    editBundleModalTitle,
    productSwapModalTitle,
    updateProduct,
    closeModal,
    swapProductForBundle,
    bundleData,
    subscription,
    product,
    updateBundle,
    shopConfiguration,
    sellingPlans,
  ]);

  React.useEffect(() => {
    if (isGlobalModalOpen && productType) {
      const modalOptions = getModalOptions();
      const modalType = modalOptions[productType];
      handleModalContent(modalType.content(), modalType.title, closeModal);
    }
  }, [
    closeModal,
    getModalOptions,
    handleModalContent,
    isGlobalModalOpen,
    productType,
  ]);
};

export const ProductOptions: React.FC<{
  showEditMyBoxModal: boolean;
  currentVariantId: string;
  subscription: any;
  shopConfiguration?: ShopConfiguration;
}> = ({
  showEditMyBoxModal,
  currentVariantId,
  subscription,
  shopConfiguration,
}) => {
  const {
    menuOptions,
    productSwapModal,
    closeModal,
    product,
    updateBundle,
    swapProductForBundle,
    updateProduct,
    sellingPlans,
  } = useProductContext() as ProductContext;
  const productType = productSwapModal as ProductSwapTypes;
  const [productId] = useState<string>('');

  const productIsBab = !!product?.CustomAttributes?.find(
    ({ key }) => key === '_bundleType' || key === '_bundleProductId',
  );
  const { authToken, customerPortalSettings, bundleIsReady } =
    useGlobalContext();

  const bundleProductId = getIdFromGid(product.ProductId) || '';
  const sellingPlan = getIdFromGid(product.SellingPlanId);
  const variant = getIdFromGid(product.VariantId);

  const { data: bundleData, isLoading } = useQuery(
    ['bundle', bundleProductId],
    async () => {
      const productsInBundle = removeOutOfStockVariants(
        transformProductsInBundle(product.CustomAttributes),
        product?.BaBOutOfStockVariants,
        shopConfiguration?.ShopOversell,
      );

      const bundle = await getBundleByProductId({
        productId: bundleProductId,
        authToken,
      });
      const bundleFormatted = await formatBundleCollections(
        bundle,
        sellingPlan as string,
        variant as string,
        productsInBundle,
      );

      return bundleFormatted;
    },
    {
      enabled: productIsBab,
    },
  );

  useLaunchModal({
    closeModal,
    product,
    sellingPlans,
    productSwapModal,
    updateBundle,
    swapProductForBundle,
    updateProduct,
    subscription,
    shopConfiguration,
    bundleData,
    isLoading,
  });

  React.useEffect(() => {
    if (showEditMyBoxModal) {
      const foundMenuOption = menuOptions.find(
        (menuOption) => menuOption.type === 'edit_my_box',
      );

      if (foundMenuOption) {
        foundMenuOption.action();
      }
    }
  }, [showEditMyBoxModal, menuOptions, productSwapModal]);
  const { t } = useTranslation();
  const isFreeProduct = itemIsFreeProduct(
    product,
    customerPortalSettings?.preventEditFreeItems,
  );

  const isRemovable = scLineModAllowed(
    product,
    SC_LINE_MODS.REMOVAL,
    SOURCE.dynamo,
  );

  const isEditable =
    !isFreeProduct &&
    scLineModAllowed(product, SC_LINE_MODS.EDIT_BUNDLE, SOURCE.dynamo);

  return menuOptions.length > 0 ? (
    <td className="body-row__options">
      <ActionsMenu
        isRemovable={isRemovable}
        isEditable={isEditable}
        menuOptions={menuOptions}
        ButtonComponent={EditButton}
        buttonDisabled={!bundleIsReady}
        text={t('tables.edit')}
      />
      {productType !== ProductSwapTypes.BUNDLE && (
        <UpdateVariant
          productId={productId as string}
          currentVariantId={currentVariantId}
          subscriptionCurrencyCode={subscription.CurrencyCode}
        />
      )}
    </td>
  ) : null;
};

const removeOutOfStockVariants = (
  variants,
  outOfStockVariantIds,
  shopOversell,
) => {
  const newVariantsIds = {};
  Object.keys(variants).forEach((variantId) => {
    if (
      !outOfStockVariantIds?.includes(variantId) ||
      shopOversell === 'ALLOW'
    ) {
      newVariantsIds[variantId] = variants[variantId];
    }
  });
  return newVariantsIds;
};
